import {
  FilterProvider,
  useFilterContext
} from "@gettactic/components/src/controls/FilterContext/FilterContext";
import {
  FilterInputHandle,
  SelectSearch
} from "@gettactic/components/src/primitives/Select/select-controls/SelectSearch";
import { Button } from "@v2/components/ui/button";
import { Checkbox } from "@v2/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger
} from "@v2/components/ui/dropdown-menu";
import { ChevronDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { WPRequestFilterBottomModalButtons } from "./WPFilterClearAndApplyFooter";
import { cn } from "@gettactic/helpers";

export const wpFilterTriggerFont = "font-normal font-brand !text-sm capitalize";

// Unified select styles that can be reused across components
export const wpRequestFilterSelectStyles = {
  // Trigger button styles
  trigger: {
    base: `min-w-[140px] justify-between ${wpFilterTriggerFont}`,
    outline: "border border-input rounded-lg rounded-md",
    disabled: "opacity-50 cursor-not-allowed bg-gray-100",
    hover: "hover:bg-gray-50",
    focus: "focus:ring-0 focus:ring-brand-electric focus:outline-none"
  },

  // Dropdown content styles
  dropdown: {
    base: "w-48 p-0 rounded-lg border border-gray-300 bg-white shadow-lg",
    header:
      "flex justify-between border-t border-gray-300 px-3 py-2 text-sm text-gray-600",
    content: "max-h-64 space-y-2 overflow-y-auto p-3",
    footer: "flex justify-between border-t border-gray-300 px-3 py-2"
  },

  // Action button styles
  actions: {
    clear:
      "text-gray-500 text-sm uppercase tracking-wider hover:text-gray-700 font-normal",
    apply:
      "text-brand-electric text-sm uppercase tracking-wider hover:bg-transparent hover:text-brand-electric",
    allNone:
      "text-brand-electric hover:text-brand-electric/80 text-sm font-normal"
  },

  // Option styles
  option: {
    container: "flex items-center space-x-2",
    label: "flex-1 cursor-pointer  text-sm text-gray-900 font-normal"
  }
};

export interface WPRequestFilterSelectOption {
  label: string;
  value: string;
  disabled?: boolean;
}

// Legacy interface for backward compatibility
export interface WPRequestDropdownFilterOption {
  label: string;
  value: string;
  disabled?: boolean;
}

export interface WPRequestFilterSelectProps {
  options: WPRequestFilterSelectOption[];
  value?: string[] | null;
  onValueChange?: (value: string[] | null) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  maxAllowed?: number;
  allowAll?: boolean;
  showSearch?: boolean;
  searchPlaceholder?: string;
  triggerClassName?: string;
  dropdownClassName?: string;
  singleSelect?: boolean;
  maxDisplayed?: number;
  // Legacy props for backward compatibility with WPRequestDropdownFilter
  tempValue?: string[];
  onValueToggle?: (value: string) => void;
  onApply?: () => void;
  onClear?: () => void;
  getDisplayText?: (filters: string[], maxDisplayed?: number) => string;
  onDropdownOpen?: (open: boolean) => void;
  showSelectionCount?: boolean;
}

// Internal component that uses FilterContext
const WPRequestFilterSelectInternal = ({
  options,
  value = [],
  onValueChange,
  placeholder = "Select options",
  className = wpRequestFilterSelectStyles.trigger.base,
  disabled = false,
  maxAllowed,
  allowAll = true,
  showSearch = false,
  searchPlaceholder = "Search...",
  triggerClassName,
  dropdownClassName,
  singleSelect = false,
  maxDisplayed = 1,
  // Legacy props for backward compatibility
  tempValue,
  onValueToggle,
  onApply,
  onClear,
  getDisplayText,
  onDropdownOpen,
  showSelectionCount = true
}: WPRequestFilterSelectProps) => {
  // Determine if we're using legacy mode (external state management)
  const isLegacyMode = !!tempValue && !!onValueToggle;

  // Local state for temporary selections (only used in non-legacy mode)
  const [tempSelectedValues, setTempSelectedValues] = useState<string[]>(
    value || []
  );
  const [isOpen, setIsOpen] = useState(false);
  const searchRef = useRef<FilterInputHandle>(null);

  // Get filter from context
  const { filter } = useFilterContext();

  // Update local state when value prop changes (only in non-legacy mode)
  useEffect(() => {
    if (!isLegacyMode) {
      setTempSelectedValues(value || []);
    }
  }, [value, isLegacyMode]);

  // Get current selected values (either from props or local state)
  const currentSelectedValues = isLegacyMode
    ? tempValue || []
    : tempSelectedValues;

  // Filter options based on search query from context
  const filteredOptions = options.filter(
    (option) =>
      !filter || option.label.toLowerCase().includes(filter.toLowerCase())
  );

  // Focus search when dropdown opens
  useEffect(() => {
    if (isOpen && showSearch) {
      searchRef.current?.focus();
    } else if (!isOpen && showSearch) {
      searchRef.current?.resetFilter();
    }
  }, [isOpen, showSearch]);

  const handleValueToggle = (optionValue: string) => {
    if (isLegacyMode) {
      // Use legacy onValueToggle callback
      onValueToggle?.(optionValue);
      return;
    }

    if (singleSelect) {
      setTempSelectedValues([optionValue]);
      onValueChange?.([optionValue]);
      return;
    }

    if (optionValue === "all") {
      setTempSelectedValues(filteredOptions.map((option) => option.value));
      return;
    }

    if (optionValue === "none") {
      setTempSelectedValues([]);
      return;
    }

    setTempSelectedValues((prev) => {
      if (prev.includes(optionValue)) {
        return prev.filter((id) => id !== optionValue);
      }
      // Check maxAllowed constraint
      if (maxAllowed && prev.length >= maxAllowed) {
        return prev;
      }
      return [...prev, optionValue];
    });
  };

  const handleApply = () => {
    if (isLegacyMode) {
      onApply?.();
    } else {
      onValueChange?.(
        tempSelectedValues.length > 0 ? tempSelectedValues : null
      );
    }
  };

  const handleClear = () => {
    if (isLegacyMode) {
      onClear?.();
    } else {
      setTempSelectedValues([]);
      onValueChange?.(null);
    }
  };

  const getDisplayTextInternal = (maxDisplayed = 1) => {
    if (!currentSelectedValues || currentSelectedValues.length === 0)
      return placeholder;

    if (singleSelect || currentSelectedValues.length === maxDisplayed) {
      const option = options.find(
        (opt) => opt.value === currentSelectedValues[0]
      );
      return option?.label || placeholder;
    }

    if (currentSelectedValues.length === options.length) {
      return "All selected";
    }

    return `${currentSelectedValues.length} selected`;
  };

  const getDisplayTextToUse = (maxDisplayed = 1) => {
    if (isLegacyMode && getDisplayText) {
      return getDisplayText(currentSelectedValues, maxDisplayed);
    }
    return getDisplayTextInternal(maxDisplayed);
  };

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        setIsOpen(open);
        onDropdownOpen?.(open);
      }}
      open={isOpen}
    >
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="default"
          className={cn(
            className,
            wpRequestFilterSelectStyles.trigger.base,
            wpRequestFilterSelectStyles.trigger.outline,
            disabled
              ? wpRequestFilterSelectStyles.trigger.disabled
              : wpRequestFilterSelectStyles.trigger.hover,
            triggerClassName
          )}
          disabled={disabled}
        >
          {getDisplayTextToUse(maxDisplayed)}
          <ChevronDown className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className={`${wpRequestFilterSelectStyles.dropdown.base} ${dropdownClassName || ""}`}
        align="start"
      >
        {/* Search input using SelectSearch component */}
        {showSearch && (
          <SelectSearch
            ref={searchRef}
            onInputChange={() => {}}
            filter={filter}
          />
        )}

        {/* Options list with blended Select All at top */}
        <div className={wpRequestFilterSelectStyles.dropdown.content}>
          {/* Select All checkbox at the top */}
          {allowAll && !singleSelect && (
            <div className={wpRequestFilterSelectStyles.option.container}>
              <Checkbox
                id="select-all"
                checked={
                  filteredOptions.length > 0 &&
                  filteredOptions.every((option) =>
                    currentSelectedValues.includes(option.value)
                  )
                }
                onCheckedChange={() => {
                  const allSelected = filteredOptions.every((option) =>
                    currentSelectedValues.includes(option.value)
                  );
                  if (allSelected) {
                    handleValueToggle("none");
                  } else {
                    handleValueToggle("all");
                  }
                }}
              />
              <label
                htmlFor="select-all"
                className={wpRequestFilterSelectStyles.option.label}
              >
                Select All
              </label>
            </div>
          )}

          {/* Regular options */}
          {filteredOptions.map((option) => (
            <div
              key={option.value}
              className={wpRequestFilterSelectStyles.option.container}
            >
              {!singleSelect && (
                <Checkbox
                  id={`option-${option.value}`}
                  checked={currentSelectedValues.includes(option.value)}
                  onCheckedChange={() => handleValueToggle(option.value)}
                  disabled={option.disabled}
                />
              )}
              <label
                htmlFor={`option-${option.value}`}
                className={`${wpRequestFilterSelectStyles.option.label} ${option.disabled ? "cursor-not-allowed opacity-50" : ""}`}
              >
                {option.label}
              </label>
            </div>
          ))}
        </div>

        {!singleSelect && (
          <WPRequestFilterBottomModalButtons
            handleApply={handleApply}
            handleClear={handleClear}
          />
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// Main component that wraps with FilterProvider
export const WPRequestFilterSelect = (props: WPRequestFilterSelectProps) => {
  return (
    <FilterProvider>
      <WPRequestFilterSelectInternal {...props} />
    </FilterProvider>
  );
};
